import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { type TableRowSelection } from 'antd/lib/table/interface';
import clsx from 'clsx';
import React, { type Key, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getAntTableScrollElement,
  useAntTableOverlayScrollbars,
} from '../../../hooks/useAntTableOverlayScrollbars';
import { useFilesTable } from '../../../hooks/useFilesTable';
import type File from '../../../interfaces/File';
import type TableEmptyState from '../../../interfaces/TableEmptyState';
import EmptyTableOverlay from '../../Common/EmptyTableOverlay';
import FilesImagePreviewGroup from '../FilesImagePreviewGroup';

const LOAD_MORE_SCROLL_THRESHOLD_PX = 100;

interface FilesTableProps {
  columns: ColumnsType<File>;
  emptyState: TableEmptyState;
  files: File[];
  handleEmptyAreaContextMenu: (event: React.MouseEvent) => void;
  handleFileRowClick: (file: File, hasClickedOnFilename: boolean, event?: React.MouseEvent) => void;
  handleFileRowDoubleClick: (file: File) => void;
  handleLoadMore: () => void;
  handleMoveDraggedFiles: (files: File[], parentFolder: File | null) => void;
  handleRowContextMenu: (record: File, event: React.MouseEvent) => void;
  handleTableChange: (pagination: unknown, tableFilters: unknown, sorter: unknown) => void;
  /** True while a list page fetch is in flight (blocks near-bottom re-check). */
  isLoading: boolean;
  /**
   * Ant Table loading overlay. Defaults to `isLoading`. Pass navigating-only so page-2+
   * fetches do not flash the full-table spinner.
   */
  showLoadingOverlay?: boolean;
  rowSelection: TableRowSelection<File>;
  selectedTableKeys: Key[];
  setSelectedTableKeys: (keys: Key[]) => void;
  tableScrollY: number;
}

function isNearBottom(scrollElement: HTMLElement): boolean {
  return (
    scrollElement.scrollTop + scrollElement.clientHeight >=
    scrollElement.scrollHeight - LOAD_MORE_SCROLL_THRESHOLD_PX
  );
}

/** OverlayScrollbars viewport when attached, else native `.ant-table-body`. */
function getFilesTableScrollElement(root: HTMLElement | null): HTMLElement | null {
  if (!root) return null;
  const body = root.querySelector<HTMLElement>('.ant-table-body');
  return getAntTableScrollElement(body);
}

/**
 * Files table: row selection, drag-and-drop, custom row actions, and shared image preview carousel.
 * Table behavior is delegated to {@link useFilesTable}.
 */
const FilesTable: React.FC<FilesTableProps> = ({
  columns,
  emptyState,
  files,
  handleEmptyAreaContextMenu,
  handleFileRowClick,
  handleFileRowDoubleClick,
  handleLoadMore,
  handleMoveDraggedFiles,
  handleRowContextMenu,
  handleTableChange,
  isLoading,
  showLoadingOverlay,
  rowSelection,
  selectedTableKeys,
  setSelectedTableKeys,
  tableScrollY,
}) => {
  const { t } = useTranslation();
  const tableLoading = showLoadingOverlay ?? isLoading;
  const { handleTableContextMenu, rowClassName, onRow } = useFilesTable({
    files,
    selectedTableKeys,
    setSelectedTableKeys,
    handleEmptyAreaContextMenu,
    handleFileRowClick,
    handleFileRowDoubleClick,
    handleRowContextMenu,
    handleMoveDraggedFiles,
  });

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [tableScrollX, setTableScrollX] = useState<number | undefined>(undefined);

  // Sync scroll.x to container width so columns can collapse responsively without a forced min width.
  useEffect(() => {
    const wrapperElement = wrapperRef.current;
    if (!wrapperElement) return;

    const updateScrollX = () => {
      const containerWidth = wrapperElement.clientWidth;
      setTableScrollX(containerWidth > 0 ? containerWidth : undefined);
    };

    updateScrollX();
    const resizeObserver = new ResizeObserver(updateScrollX);
    resizeObserver.observe(wrapperElement);
    return () => resizeObserver.disconnect();
  }, []);

  const checkLoadMore = useCallback(
    (scrollElement: HTMLElement) => {
      if (isNearBottom(scrollElement)) {
        handleLoadMore();
      }
    },
    [handleLoadMore],
  );

  const isTableEmpty = files.length === 0;

  // Scroll lives on OverlayScrollbars viewport (body host has overflow:hidden).
  useAntTableOverlayScrollbars(wrapperRef, {
    onScroll: checkLoadMore,
  });

  // ponytail: scroll events fire only on movement; re-check after append when user stays at bottom.
  useEffect(() => {
    if (isLoading) return;
    const scrollElement = getFilesTableScrollElement(wrapperRef.current);
    if (!scrollElement) return;
    checkLoadMore(scrollElement);
  }, [files.length, isLoading, checkLoadMore]);

  return (
    <div
      ref={wrapperRef}
      className="relative flex h-full w-full flex-col"
      onContextMenu={handleTableContextMenu}
    >
      <FilesImagePreviewGroup files={files}>
        <Table<File>
          className={clsx(
            'custom-table select-none',
            isTableEmpty && 'pointer-events-none opacity-0',
          )}
          columns={columns}
          dataSource={files}
          loading={tableLoading}
          locale={{
            emptyText: (
              <span aria-hidden="true" className="invisible">
                {'\u00A0'}
              </span>
            ),
          }}
          onChange={handleTableChange}
          onRow={onRow}
          pagination={false}
          rowClassName={rowClassName}
          rowKey="id"
          scroll={{ x: tableScrollX, y: tableScrollY }}
          showSorterTooltip={false}
          size="large"
          aria-label={t('a11y.filesTable')}
          rowSelection={rowSelection}
        />
      </FilesImagePreviewGroup>

      <EmptyTableOverlay
        icon={emptyState.iconName}
        label={emptyState.label || t('a11y.emptyFilesTable')}
        isVisible={isTableEmpty}
        isLoading={tableLoading}
        variant={emptyState.variant}
        onRetry={emptyState.onRetry}
      />
      {isTableEmpty && !isLoading && (
        <div role="status" className="sr-only">
          {emptyState.label || t('a11y.emptyFilesTable')}
        </div>
      )}
    </div>
  );
};

export default FilesTable;
